/**
 * ESPACE PARENT — profil "Papa" (PIN 1984). Trois parties :
 *  1. DASHBOARD — résumé de progression par enfant + la file "à papa" (les moments
 *     que JARVIS a signalés : étapes, inquiétudes, grandes questions, moments drôles),
 *     avec la citation exacte de l'enfant. Remplace le bot Telegram.
 *  2. RITUELS — éditeur des checklists matin/soir par enfant (POST /api/parent/rituals).
 *  3. MODE TEST — la vraie console enfant sous l'identité isolée "papa" (voir childSession).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import KidProfile from "../profiles/KidProfile";
import { setTestMode } from "./childSession";

type ResetState = "idle" | "working" | "done" | "error";
type ChildSummary = { id: string; name: string; rank: string; correctCount: number; creatures: number; fusions: number };
type BriefItem = { id: string; childId: string; child: string; category: string; summary: string; quote: string; ts: string };
type Dashboard = { children: ChildSummary[]; brief: BriefItem[] };
type Rituals = { morning: string[]; evening: string[] };
type AllRituals = { isaac: Rituals; bao: Rituals };

const CAT: Record<string, { icon: string; label: string; color: string }> = {
  milestones: { icon: "🏆", label: "Étape", color: "#2eff9e" },
  worries: { icon: "⚠️", label: "À surveiller", color: "#ff9d3c" },
  "big-questions": { icon: "💭", label: "Grande question", color: "#00d4ff" },
  funny: { icon: "😄", label: "Moment drôle", color: "#ffd43b" },
};
const catOf = (c: string) => CAT[c] || { icon: "📝", label: "Note", color: "#8896a8" };

function whenStr(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleString("fr-FR", { weekday: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}
const AV: Record<string, string> = { isaac: "🦖", bao: "🦄" };
const CHILD_NAMES: Record<string, string> = { isaac: "Isaac", bao: "Bao" };

// ── RITUAL EDITOR ─────────────────────────────────────────────────────────────

type RitualSaveState = "idle" | "saving" | "saved" | "error";

function RitualEditor({
  childId,
  moment,
  items,
  onChange,
}: {
  childId: string;
  moment: "morning" | "evening";
  items: string[];
  onChange: (items: string[]) => void;
}) {
  const [saveState, setSaveState] = useState<RitualSaveState>("idle");
  const [localItems, setLocalItems] = useState<string[]>(items);
  const [newItem, setNewItem] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // sync when parent changes child/moment
  useEffect(() => { setLocalItems(items); setSaveState("idle"); }, [items]);

  const reorder = (from: number, to: number) => {
    const next = [...localItems];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setLocalItems(next);
    setSaveState("idle");
  };
  const remove = (i: number) => {
    setLocalItems((prev) => prev.filter((_, idx) => idx !== i));
    setSaveState("idle");
  };
  const addItem = () => {
    const v = newItem.trim().slice(0, 60);
    if (!v) return;
    setLocalItems((prev) => [...prev, v]);
    setNewItem("");
    setSaveState("idle");
    inputRef.current?.focus();
  };
  const save = async () => {
    setSaveState("saving");
    try {
      const r = await fetch("/api/parent/rituals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ childId, moment, items: localItems }),
      });
      if (r.ok) { setSaveState("saved"); onChange(localItems); setTimeout(() => setSaveState("idle"), 2000); }
      else setSaveState("error");
    } catch { setSaveState("error"); }
  };

  const label = moment === "morning" ? "Matin" : "Soir";
  const icon = moment === "morning" ? "🌅" : "🌙";

  return (
    <div style={RS.panel}>
      <div style={RS.panelHead}>{icon} Rituel {label}</div>
      <div style={RS.list}>
        {localItems.map((item, i) => (
          <div key={i} style={RS.row}>
            <span style={RS.num}>{i + 1}</span>
            <span style={RS.itemText}>{item}</span>
            <div style={RS.rowBtns}>
              {i > 0 && <button style={RS.iconBtn} onClick={() => reorder(i, i - 1)} title="Monter">↑</button>}
              {i < localItems.length - 1 && <button style={RS.iconBtn} onClick={() => reorder(i, i + 1)} title="Descendre">↓</button>}
              <button style={{ ...RS.iconBtn, color: "#ff6b6b" }} onClick={() => remove(i)} title="Supprimer">✕</button>
            </div>
          </div>
        ))}
        {localItems.length === 0 && <div style={RS.empty}>Aucune étape — ajoutes-en ci-dessous</div>}
      </div>
      <div style={RS.addRow}>
        <input
          ref={inputRef}
          style={RS.input}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addItem()}
          placeholder="Nouvelle étape…"
          maxLength={60}
        />
        <button style={RS.addBtn} onClick={addItem}>+</button>
      </div>
      <button
        style={{
          ...RS.saveBtn,
          background: saveState === "saved" ? "#2eff9e22" : saveState === "error" ? "#ff6b6b22" : "#f0b03a22",
          borderColor: saveState === "saved" ? "#2eff9e" : saveState === "error" ? "#ff6b6b" : "#f0b03a",
          color: saveState === "saved" ? "#2eff9e" : saveState === "error" ? "#ff6b6b" : "#f0b03a",
        }}
        onClick={save}
        disabled={saveState === "saving"}
      >
        {saveState === "saving" ? "Enregistrement…" : saveState === "saved" ? "✓ Enregistré" : saveState === "error" ? "⚠️ Erreur" : "💾 Enregistrer ce rituel"}
      </button>
    </div>
  );
}

const RS: Record<string, React.CSSProperties> = {
  panel: { background: "rgba(20,16,10,0.7)", border: "1px solid #f0b03a33", borderRadius: 14, padding: "14px 14px 10px", marginBottom: 12 },
  panelHead: { fontSize: 14, fontWeight: 700, color: "#e9d6a8", marginBottom: 10 },
  list: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 },
  row: { display: "flex", alignItems: "center", gap: 8, background: "rgba(240,176,58,0.06)", borderRadius: 9, padding: "7px 10px" },
  num: { color: "#f0b03a", fontWeight: 700, fontSize: 12, minWidth: 18, textAlign: "center" },
  itemText: { flex: 1, fontSize: 14, color: "#e9d6a8" },
  rowBtns: { display: "flex", gap: 4 },
  iconBtn: { background: "transparent", border: "none", color: "#b7a888", fontSize: 14, cursor: "pointer", padding: "2px 5px", borderRadius: 6 },
  empty: { color: "#8c8168", fontSize: 13, padding: "8px 4px", fontStyle: "italic" },
  addRow: { display: "flex", gap: 8, marginBottom: 10 },
  input: { flex: 1, background: "rgba(20,16,10,0.8)", border: "1px solid #f0b03a44", borderRadius: 9, color: "#e9d6a8", fontSize: 14, padding: "8px 12px", outline: "none" },
  addBtn: { background: "#f0b03a", color: "#1a1205", border: "none", borderRadius: 9, width: 36, fontSize: 20, fontWeight: 800, cursor: "pointer" },
  saveBtn: { width: "100%", padding: "10px", borderRadius: 10, border: "1px solid", fontSize: 14, fontWeight: 700, cursor: "pointer", transition: "all 0.2s" },
};

// ── RITUALS VIEW ──────────────────────────────────────────────────────────────

function RitualsView({ onBack }: { onBack: () => void }) {
  const [allRituals, setAllRituals] = useState<AllRituals | null>(null);
  const [activeChild, setActiveChild] = useState<"isaac" | "bao">("isaac");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/parent/rituals");
        if (r.ok) setAllRituals(await r.json());
      } catch { /* offline */ }
      setLoading(false);
    })();
  }, []);

  const update = (cid: "isaac" | "bao", moment: "morning" | "evening", items: string[]) => {
    setAllRituals((prev) => prev ? { ...prev, [cid]: { ...prev[cid], [moment]: items } } : prev);
  };

  const rituals = allRituals?.[activeChild] ?? { morning: [], evening: [] };

  return (
    <div style={S.screen}>
      <div style={S.topbar}>
        <button style={S.exitTop} onClick={onBack}>← Dashboard</button>
        <div style={S.badge}>RITUELS</div>
        <div style={{ width: 80 }} />
      </div>
      <div style={S.scroll}>
        {/* Sélecteur enfant */}
        <div style={{ display: "flex", gap: 10, margin: "10px 0 16px" }}>
          {(["isaac", "bao"] as const).map((cid) => (
            <button
              key={cid}
              onClick={() => setActiveChild(cid)}
              style={{
                flex: 1, padding: "11px", borderRadius: 12, border: `2px solid ${activeChild === cid ? GOLD : GOLD + "33"}`,
                background: activeChild === cid ? GOLD + "18" : "transparent", color: activeChild === cid ? GOLD : "#b7a888",
                fontWeight: 700, fontSize: 15, cursor: "pointer",
              }}
            >
              {AV[cid]} {CHILD_NAMES[cid]}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ color: "#8c8168", fontSize: 14, textAlign: "center", padding: 30 }}>Chargement…</div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: "#8c8168", marginBottom: 14, lineHeight: 1.5 }}>
              Ces étapes sont lues par JARVIS quand {CHILD_NAMES[activeChild]} dit « on fait le rituel du matin » ou « du soir ». Tu peux les réorganiser, en retirer ou en ajouter (max 12 par rituel, 60 caractères par étape).
            </div>
            <RitualEditor
              key={`${activeChild}-morning`}
              childId={activeChild}
              moment="morning"
              items={rituals.morning}
              onChange={(items) => update(activeChild, "morning", items)}
            />
            <RitualEditor
              key={`${activeChild}-evening`}
              childId={activeChild}
              moment="evening"
              items={rituals.evening}
              onChange={(items) => update(activeChild, "evening", items)}
            />
          </>
        )}
        <div style={{ height: 40 }} />
      </div>
    </div>
  );
}

// ── REPLY BOX — réponses rapides à l'enfant. Le message est annoncé par JARVIS
// (voix + carte 💌) à la prochaine session de l'enfant. POST /api/parent/reply.
const QUICK_REPLIES = ["Bravo, je suis fier de toi ! 👏", "Je t'aime fort ❤️", "On en parle ce soir 😊", "Bien reçu ! 👍"];

function ReplyBox({ childId, childName }: { childId: string; childName: string }) {
  const [text, setText] = useState("");
  const [sendState, setSendState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const send = async (msg: string) => {
    const v = msg.trim().slice(0, 300);
    if (!v || sendState === "sending") return;
    setSendState("sending");
    try {
      const r = await fetch("/api/parent/reply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ childId, text: v }),
      });
      if (r.ok) { setSendState("sent"); setText(""); setTimeout(() => setSendState("idle"), 2400); }
      else setSendState("error");
    } catch { setSendState("error"); }
  };
  return (
    <div style={RB.box}>
      <div style={RB.chips}>
        {QUICK_REPLIES.map((q) => (
          <button key={q} style={RB.chip} onClick={() => send(q)} disabled={sendState === "sending"}>{q}</button>
        ))}
      </div>
      <div style={RB.row}>
        <input
          style={RB.input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send(text)}
          placeholder={`Message pour ${childName}…`}
          maxLength={300}
        />
        <button style={RB.sendBtn} onClick={() => send(text)} disabled={sendState === "sending"}>
          {sendState === "sending" ? "…" : "Envoyer"}
        </button>
      </div>
      {sendState === "sent" && <div style={{ color: "#2eff9e", fontSize: 12, marginTop: 6 }}>✓ Envoyé — JARVIS le transmettra à {childName} à sa prochaine session.</div>}
      {sendState === "error" && <div style={{ color: "#ff6b6b", fontSize: 12, marginTop: 6 }}>⚠️ Échec de l'envoi — réessaie.</div>}
    </div>
  );
}

const RB: Record<string, React.CSSProperties> = {
  box: { marginTop: 10, paddingTop: 10, borderTop: "1px solid #f0b03a22" },
  chips: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 },
  chip: { background: "rgba(240,176,58,0.1)", border: "1px solid #f0b03a44", color: "#e9d6a8", borderRadius: 999, padding: "5px 11px", fontSize: 12.5, cursor: "pointer" },
  row: { display: "flex", gap: 8 },
  input: { flex: 1, background: "rgba(20,16,10,0.8)", border: "1px solid #f0b03a44", borderRadius: 9, color: "#e9d6a8", fontSize: 14, padding: "8px 12px", outline: "none" },
  sendBtn: { background: "#f0b03a", color: "#1a1205", border: "none", borderRadius: 9, padding: "0 14px", fontSize: 13, fontWeight: 800, cursor: "pointer" },
};

// ── OBJECTIFS/CORVÉES — Pat en donne, valide "fait", récompense créature garantie
// livrée à la session suivante de l'enfant. GET/POST /api/parent/objectives.
type Objective = { id: string; label: string; status: "open" | "done_pending" | "validated"; rewardPending?: boolean };
type AllObjectives = { isaac: Objective[]; bao: Objective[] };

function ObjectivesView({ onBack }: { onBack: () => void }) {
  const [all, setAll] = useState<AllObjectives | null>(null);
  const [activeChild, setActiveChild] = useState<"isaac" | "bao">("isaac");
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch("/api/parent/objectives");
      if (r.ok) setAll(await r.json());
    } catch { /* offline */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (action: string, extra: Record<string, any> = {}) => {
    setBusy(true);
    try {
      const r = await fetch("/api/parent/objectives", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ childId: activeChild, action, ...extra }),
      });
      if (r.ok) {
        const d = await r.json();
        setAll((prev) => (prev ? { ...prev, [activeChild]: d.items } : prev));
      }
    } catch { /* offline */ }
    setBusy(false);
  };

  const add = async () => {
    const v = newLabel.trim();
    if (!v || busy) return;
    setNewLabel("");
    await act("add", { label: v });
  };

  const items = all?.[activeChild] || [];
  const STATUS_LABEL: Record<string, { icon: string; text: string; color: string }> = {
    open: { icon: "⏳", text: "En cours", color: "#8c8168" },
    done_pending: { icon: "🙋", text: "À valider !", color: "#f0b03a" },
    validated: { icon: "✅", text: "Validé", color: "#2eff9e" },
  };

  return (
    <div style={S.screen}>
      <div style={S.topbar}>
        <button style={S.exitTop} onClick={onBack}>← Dashboard</button>
        <div style={S.badge}>OBJECTIFS</div>
        <div style={{ width: 80 }} />
      </div>
      <div style={S.scroll}>
        <div style={{ display: "flex", gap: 10, margin: "10px 0 16px" }}>
          {(["isaac", "bao"] as const).map((cid) => (
            <button
              key={cid}
              onClick={() => setActiveChild(cid)}
              style={{
                flex: 1, padding: "11px", borderRadius: 12, border: `2px solid ${activeChild === cid ? GOLD : GOLD + "33"}`,
                background: activeChild === cid ? GOLD + "18" : "transparent", color: activeChild === cid ? GOLD : "#b7a888",
                fontWeight: 700, fontSize: 15, cursor: "pointer",
              }}
            >
              {AV[cid]} {CHILD_NAMES[cid]}
            </button>
          ))}
        </div>

        <div style={{ fontSize: 12, color: "#8c8168", marginBottom: 14, lineHeight: 1.5 }}>
          Donne un objectif ou une petite corvée. JARVIS l'encourage ; quand {CHILD_NAMES[activeChild]} dit « j'ai fait », ça remonte ici pour ta validation — une créature garantie l'attendra à sa prochaine session.
        </div>

        <div style={RB.row}>
          <input
            style={RB.input}
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Nouvel objectif ou corvée…"
            maxLength={80}
          />
          <button style={RB.sendBtn} onClick={add} disabled={busy}>+ Ajouter</button>
        </div>

        <div style={{ height: 16 }} />

        {items.length === 0 && <div style={S.empty}>Aucun objectif pour {CHILD_NAMES[activeChild]} — ajoutes-en un ci-dessus.</div>}
        {items.map((it) => {
          const st = STATUS_LABEL[it.status];
          return (
            <div key={it.id} style={{ ...S.msg, borderLeftColor: st.color }}>
              <div style={S.msgHead}>
                <span style={{ color: st.color, fontWeight: 700 }}>{st.icon} {st.text}</span>
                {it.rewardPending && <span style={S.msgWho}>🎁 récompense en attente</span>}
              </div>
              <div style={{ ...S.quote, fontStyle: "normal", fontSize: 15 }}>{it.label}</div>
              <div style={{ display: "flex", gap: 8 }}>
                {it.status === "done_pending" && (
                  <>
                    <button style={{ ...S.dismiss, borderColor: "#2eff9e88", color: "#2eff9e" }} onClick={() => act("validate", { id: it.id })} disabled={busy}>✓ Valider</button>
                    <button style={S.dismiss} onClick={() => act("reject", { id: it.id })} disabled={busy}>↩ Pas encore</button>
                  </>
                )}
                <button style={S.dismiss} onClick={() => act("remove", { id: it.id })} disabled={busy}>🗑 Retirer</button>
              </div>
            </div>
          );
        })}
        <div style={{ height: 40 }} />
      </div>
    </div>
  );
}

type JournalItem = { id: string; type: string; caption: string; at: number; text: string | null; url: string | null; childId: string; child: string };
const JOURNAL_TYPE_LABEL: Record<string, { icon: string; label: string }> = {
  text: { icon: "✍️", label: "Texte" },
  drawing: { icon: "🎨", label: "Dessin" },
  audio: { icon: "🎤", label: "Audio" },
  photo: { icon: "📸", label: "Photo" },
  video: { icon: "🎥", label: "Vidéo" },
};

function JournalView({ onBack }: { onBack: () => void }) {
  const [items, setItems] = useState<JournalItem[] | null>(null);
  const [filter, setFilter] = useState<"all" | "isaac" | "bao">("all");

  useEffect(() => {
    fetch("/api/parent/journal")
      .then((r) => r.json())
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]));
  }, []);

  const shown = (items || []).filter((it) => filter === "all" || it.childId === filter);

  return (
    <div style={S.screen}>
      <div style={S.topbar}>
        <button style={S.exitTop} onClick={onBack}>← Dashboard</button>
        <div style={S.badge}>JOURNAL</div>
        <div style={{ width: 80 }} />
      </div>
      <div style={S.scroll}>
        <div style={{ display: "flex", gap: 8, margin: "10px 0 16px" }}>
          {(["all", "isaac", "bao"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                flex: 1, padding: "9px", borderRadius: 12, border: `2px solid ${filter === f ? GOLD : GOLD + "33"}`,
                background: filter === f ? GOLD + "18" : "transparent", color: filter === f ? GOLD : "#b7a888",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
            >
              {f === "all" ? "Tous" : AV[f]} {f === "isaac" ? "Isaac" : f === "bao" ? "Bao" : ""}
            </button>
          ))}
        </div>

        {items === null && <div style={S.empty}>Chargement…</div>}
        {items !== null && shown.length === 0 && <div style={S.empty}>Rien pour l'instant.</div>}
        {shown.map((it) => {
          const t = JOURNAL_TYPE_LABEL[it.type] || { icon: "📝", label: it.type };
          return (
            <div key={it.id} style={{ ...S.msg, borderLeftColor: GOLD }}>
              <div style={S.msgHead}>
                <span style={{ color: GOLD, fontWeight: 700 }}>{t.icon} {t.label}</span>
                <span style={S.msgWho}>{AV[it.childId] || ""} {it.child} · {whenStr(new Date(it.at).toISOString())}</span>
              </div>
              {it.type === "text" && it.text && <div style={{ ...S.quote, fontStyle: "normal" }}>{it.text}</div>}
              {(it.type === "drawing" || it.type === "photo") && it.url && (
                <img src={it.url} alt="" style={{ width: "100%", borderRadius: 10, marginTop: 6 }} />
              )}
              {it.type === "audio" && it.url && <audio src={it.url} controls style={{ width: "100%", marginTop: 6 }} />}
              {it.type === "video" && it.url && <video src={it.url} controls style={{ width: "100%", borderRadius: 10, marginTop: 6 }} />}
            </div>
          );
        })}
        <div style={{ height: 40 }} />
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export function ParentSpace({ onExit }: { onExit: () => void }) {
  const [view, setView] = useState<"menu" | "rituals" | "objectives" | "journal" | "test">("menu");
  const [reset, setReset] = useState<ResetState>("idle");
  const [data, setData] = useState<Dashboard | null>(null);
  // Réponses rapides : quel message a sa boîte de réponse ouverte / quel enfant
  // a sa boîte "écrire un message" ouverte (depuis sa carte).
  const [replyOpen, setReplyOpen] = useState<string | null>(null);
  const [writeTo, setWriteTo] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch("/api/parent/dashboard");
      if (r.ok) setData(await r.json());
    } catch {
      /* offline */
    }
  }, []);
  useEffect(() => {
    if (view === "menu") load();
  }, [view, load]);

  const enterTest = () => { setTestMode(true); setView("test"); };
  const leaveTest = () => { setTestMode(false); setView("menu"); };

  const resetTest = async () => {
    setReset("working");
    try {
      const r = await fetch("/api/test/reset", { method: "POST" });
      setReset(r.ok ? "done" : "error");
    } catch { setReset("error"); }
    setTimeout(() => setReset("idle"), 2600);
  };

  const dismiss = async (id: string) => {
    setData((d) => (d ? { ...d, brief: d.brief.filter((b) => b.id !== id) } : d));
    try { await fetch("/api/parent/brief/dismiss", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }) }); } catch { /* ignore */ }
  };

  // ── TEST VIEW ────────────────────────────────────────────────────────────────
  if (view === "test") {
    return (
      <div style={{ position: "fixed", inset: 0 }}>
        <KidProfile />
        <button style={S.leaveTest} onClick={leaveTest}>← Quitter le test</button>
      </div>
    );
  }

  // ── RITUALS VIEW ─────────────────────────────────────────────────────────────
  if (view === "rituals") {
    return <RitualsView onBack={() => setView("menu")} />;
  }

  // ── OBJECTIVES VIEW ──────────────────────────────────────────────────────────
  if (view === "objectives") {
    return <ObjectivesView onBack={() => setView("menu")} />;
  }

  // ── JOURNAL VIEW ─────────────────────────────────────────────────────────────
  if (view === "journal") {
    return <JournalView onBack={() => setView("menu")} />;
  }

  // ── DASHBOARD VIEW ───────────────────────────────────────────────────────────
  const brief = data?.brief || [];
  return (
    <div style={S.screen}>
      <div style={S.topbar}>
        <div style={S.badge}>ESPACE PARENT</div>
        <button style={S.exitTop} onClick={onExit}>← Profils</button>
      </div>

      <div style={S.scroll}>
        {/* Enfants */}
        <div style={S.grid2}>
          {(data?.children || []).map((c) => (
            <div key={c.id} style={S.childCard}>
              <div style={{ fontSize: 30 }}>{AV[c.id] || "🧒"}</div>
              <div style={S.childName}>{c.name}</div>
              <div style={S.childRank}>{c.rank}</div>
              <div style={S.childStats}>
                {c.correctCount} bonnes rép. · {c.creatures} créature{c.creatures > 1 ? "s" : ""}
                {c.fusions > 0 ? ` · ${c.fusions} fusion${c.fusions > 1 ? "s" : ""}` : ""}
              </div>
              <button style={S.writeBtn} onClick={() => setWriteTo((cur) => (cur === c.id ? null : c.id))}>
                💌 Écrire
              </button>
            </div>
          ))}
        </div>
        {writeTo && (
          <div style={{ ...S.msg, borderLeftColor: GOLD, marginTop: 10 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#e9d6a8" }}>
              💌 Message pour {CHILD_NAMES[writeTo] || writeTo} — JARVIS l'annoncera à sa prochaine session
            </div>
            <ReplyBox childId={writeTo} childName={CHILD_NAMES[writeTo] || writeTo} />
          </div>
        )}

        {/* Messages à papa */}
        <div style={S.sectionH}>💌 Messages de tes enfants {brief.length > 0 && <span style={S.count}>{brief.length}</span>}</div>
        {brief.length === 0 && <div style={S.empty}>Aucun message pour l'instant. Quand JARVIS repère un moment marquant, drôle ou une question, il apparaîtra ici.</div>}
        {brief.map((b) => {
          const c = catOf(b.category);
          return (
            <div key={b.id} style={{ ...S.msg, borderLeftColor: c.color }}>
              <div style={S.msgHead}>
                <span style={{ color: c.color, fontWeight: 700 }}>{c.icon} {c.label}</span>
                <span style={S.msgWho}>{AV[b.childId] || ""} {b.child} · {whenStr(b.ts)}</span>
              </div>
              {b.quote && <div style={S.quote}>« {b.quote} »</div>}
              {b.summary && <div style={S.summary}>{b.summary}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <button style={S.dismiss} onClick={() => dismiss(b.id)}>✓ Vu</button>
                <button style={S.dismiss} onClick={() => setReplyOpen((cur) => (cur === b.id ? null : b.id))}>
                  ↩️ Répondre
                </button>
              </div>
              {replyOpen === b.id && <ReplyBox childId={b.childId} childName={b.child} />}
            </div>
          );
        })}

        {/* Actions */}
        <div style={S.divider} />

        {/* Rituels */}
        <button style={S.ritualsBtn} onClick={() => setView("rituals")}>
          <span style={{ fontSize: 22 }}>🌅🌙</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Rituels matin / soir</div>
            <div style={{ fontSize: 12, color: "#b7a888", marginTop: 2 }}>Personnaliser les checklists d'Isaac et Bao</div>
          </div>
          <span style={{ color: GOLD, fontSize: 18 }}>›</span>
        </button>

        <button style={S.ritualsBtn} onClick={() => setView("objectives")}>
          <span style={{ fontSize: 22 }}>🎯</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Objectifs / corvées</div>
            <div style={{ fontSize: 12, color: "#b7a888", marginTop: 2 }}>Donner un objectif, valider ce qui est fait</div>
          </div>
          <span style={{ color: GOLD, fontSize: 18 }}>›</span>
        </button>

        <button style={S.ritualsBtn} onClick={() => setView("journal")}>
          <span style={{ fontSize: 22 }}>📔</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Journal</div>
            <div style={{ fontSize: 12, color: "#b7a888", marginTop: 2 }}>Texte, dessins, audio, photos, vidéos — tout ce qu'ils y mettent</div>
          </div>
          <span style={{ color: GOLD, fontSize: 18 }}>›</span>
        </button>

        <button style={S.testBtn} onClick={enterTest}>🧪 Mode Test — jouer comme un enfant (isolé)</button>
        <button style={S.resetBtn} onClick={resetTest} disabled={reset === "working"}>
          {reset === "working" ? "Réinitialisation…" : reset === "done" ? "✓ Test remis à zéro" : reset === "error" ? "⚠️ Échec" : "🔄 Rejouer le test depuis zéro"}
        </button>
        <div style={{ height: 40 }} />
      </div>
    </div>
  );
}

const GOLD = "#f0b03a";
const S: Record<string, React.CSSProperties> = {
  screen: { position: "fixed", inset: 0, background: "radial-gradient(120% 120% at 50% 0%, #14100a 0%, #05070a 60%)", display: "flex", flexDirection: "column", fontFamily: "system-ui, sans-serif", color: "#e9d6a8" },
  topbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px 10px", borderBottom: `1px solid ${GOLD}33` },
  badge: { color: GOLD, letterSpacing: "0.26em", fontSize: 13, fontWeight: 700 },
  exitTop: { background: "transparent", border: `1px solid ${GOLD}55`, color: GOLD, borderRadius: 12, padding: "7px 12px", fontSize: 13, cursor: "pointer" },
  scroll: { flex: 1, overflowY: "auto", padding: "14px 16px 0" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  childCard: { background: "rgba(20,16,10,0.7)", border: `1px solid ${GOLD}33`, borderRadius: 16, padding: "14px 12px", textAlign: "center" },
  childName: { fontSize: 17, fontWeight: 800, marginTop: 4 },
  childRank: { color: GOLD, fontSize: 13, fontWeight: 600, marginTop: 2 },
  childStats: { color: "#b7a888", fontSize: 12, marginTop: 6, lineHeight: 1.4 },
  sectionH: { fontSize: 15, fontWeight: 700, color: "#e9d6a8", margin: "22px 2px 12px", display: "flex", alignItems: "center", gap: 8 },
  count: { background: GOLD, color: "#1a1205", borderRadius: 999, fontSize: 12, fontWeight: 800, padding: "1px 8px" },
  empty: { color: "#8c8168", fontSize: 14, lineHeight: 1.5, background: "rgba(20,16,10,0.5)", border: `1px solid ${GOLD}22`, borderRadius: 14, padding: "16px" },
  msg: { background: "rgba(20,16,10,0.7)", borderRadius: 14, borderLeft: "4px solid", padding: "12px 14px", marginBottom: 10 },
  msgHead: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5, marginBottom: 6, gap: 8, flexWrap: "wrap" },
  msgWho: { color: "#9a8e6f", fontSize: 12 },
  quote: { color: "#f3e6c4", fontSize: 16, fontStyle: "italic", lineHeight: 1.45, margin: "2px 0 6px" },
  summary: { color: "#b7a888", fontSize: 13, lineHeight: 1.45 },
  dismiss: { marginTop: 8, background: "transparent", border: `1px solid ${GOLD}44`, color: GOLD, borderRadius: 10, padding: "5px 12px", fontSize: 12, cursor: "pointer" },
  writeBtn: { marginTop: 8, background: "transparent", border: `1px solid ${GOLD}44`, color: GOLD, borderRadius: 10, padding: "5px 12px", fontSize: 12, cursor: "pointer", width: "100%" },
  divider: { height: 1, background: `${GOLD}22`, margin: "22px 0 16px" },
  ritualsBtn: {
    width: "100%", padding: "14px 16px", borderRadius: 14, border: `1px solid ${GOLD}44`,
    background: `${GOLD}0e`, color: "#e9d6a8", fontSize: 15, cursor: "pointer",
    display: "flex", alignItems: "center", gap: 14, marginBottom: 10, textAlign: "left",
  },
  testBtn: { width: "100%", padding: "15px", borderRadius: 14, border: "none", background: `linear-gradient(180deg, ${GOLD}, #d69a2a)`, color: "#1a1205", fontSize: 16, fontWeight: 800, cursor: "pointer" },
  resetBtn: { width: "100%", marginTop: 10, padding: "12px", borderRadius: 12, border: `1px solid ${GOLD}55`, background: "transparent", color: "#e9d6a8", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  leaveTest: {
    position: "fixed", bottom: "calc(max(16px, env(safe-area-inset-bottom)) + 30px)", left: "50%",
    transform: "translateX(-50%)", zIndex: 9999, padding: "9px 16px", borderRadius: 12,
    border: `1px solid ${GOLD}88`, background: "rgba(10,8,4,0.82)", color: GOLD, fontSize: 13,
    fontWeight: 700, cursor: "pointer", backdropFilter: "blur(4px)", whiteSpace: "nowrap",
  },
};
